import React, { useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Chunk, Change } from '@/types/parse-diff';
import Line from './Line';

interface VirtualizedUnifiedListProps {
  chunks: Chunk[];
  filePath: string;
}

const getLanguageFromPath = (filePath: string) => {
  const extension = filePath.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'js':
    case 'jsx':
      return 'javascript';
    case 'ts':
    case 'tsx':
      return 'typescript';
    case 'py':
      return 'python';
    case 'java':
      return 'java';
    case 'go':
      return 'go';
    case 'css':
      return 'css';
    case 'html':
      return 'html';
    case 'json':
      return 'json';
    case 'md':
      return 'markdown';
    case 'sh':
      return 'bash';
    default:
      return 'plaintext';
  }
};

const VirtualizedUnifiedList: React.FC<VirtualizedUnifiedListProps> = ({
  chunks,
  filePath,
}) => {
  const language = getLanguageFromPath(filePath);
  const parentRef = useRef<HTMLDivElement>(null);

  const lines = useMemo(() => {
    const allLines: (Change | { isHeader: true; content: string })[] = [];
    chunks.forEach((chunk) => {
      allLines.push({ isHeader: true, content: chunk.content });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      chunk.changes.forEach((change: any) => {
        allLines.push(change);
      });
    });
    return allLines;
  }, [chunks]);

  const rowVirtualizer = useVirtualizer({
    count: lines.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 16,
    overscan: 10,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  const Row = ({ line, language }: { line: Change | { isHeader: true; content: string }; language: string }) => {
    if ('isHeader' in line) {
      return (
        <div className="bg-blue-900/20 text-blue-300 text-xs p-1 font-mono select-none">
          {line.content}
        </div>
      );
    }
    return <Line change={line} language={language} />;
  };

  return (
    <div ref={parentRef} className="h-full w-full overflow-auto bg-card">
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualItems.map((virtualRow) => {
          const line = lines[virtualRow.index];
          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={rowVirtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <Row line={line} language={language} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VirtualizedUnifiedList;
